import React, { useState, useMemo } from 'react'
import {
    Button,
    Checkbox,
    CircularProgress,
    Divider,
    IconButton,
    MenuItem,
    Modal,
    Paper,
    Select,
    Slide,
    TextField,
    InputAdornment,
    Tooltip,
    useTheme,
} from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import SearchIcon from '@material-ui/icons/Search'
import ArchiveIcon from '@material-ui/icons/Archive'
import WarningIcon from '@material-ui/icons/Warning'
import ArrowBackIcon from '@material-ui/icons/ArrowBack'
import { Container } from '../../components'
import { useUser } from '../../hooks'
import { User } from '../../models'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../store'
import { typographyStyle } from '../../theme/typographyStyle'
import { toast } from 'react-toastify'
import { EDIT_USER } from '../../models/Permissions'
import { hasPermission } from '../../models/Users/services'
import { axiosInstance } from '../../helpers'
import { removeUserFromList } from '../../store/user/action'

interface Props {
    open: boolean
    handleClose: () => void
    userList: User[]
}

type ArchiveStatus = 'idle' | 'loading' | 'success' | 'error'
type Step = 'select' | 'confirm'

export const BulkArchiveUsersModal = ({
    open,
    handleClose,
    userList,
}: Props) => {
    const theme = useTheme()
    const dispatch = useDispatch()
    const { workspaceUser } = useUser()

    // Mirrors UserModal: archiving is a user-edit action, gated behind EDIT_USER
    const canEditUsers = hasPermission(workspaceUser, EDIT_USER)

    const permissionRoles = useSelector(
        (state: RootState) => state.permission.permissionRoleList,
    )

    const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set())
    const [searchText, setSearchText] = useState('')
    const [selectedRoleId, setSelectedRoleId] = useState<number | ''>('')
    const [archiveStatuses, setArchiveStatuses] = useState<
        Record<number, ArchiveStatus>
    >({})
    const [isArchiving, setIsArchiving] = useState(false)
    const [step, setStep] = useState<Step>('select')

    const eligibleUsers = useMemo(
        () =>
            userList.filter(
                (u) =>
                    !u.workspace_owner &&
                    u.id !== workspaceUser?.id &&
                    archiveStatuses[u.id] !== 'success',
            ),
        [userList, workspaceUser, archiveStatuses],
    )

    const filteredUsers = useMemo(
        () =>
            eligibleUsers.filter((u) => {
                const matchesSearch = u.name
                    .toLowerCase()
                    .includes(searchText.toLowerCase())
                const matchesRole =
                    selectedRoleId === '' ||
                    u.permission_roles.some((r) => r.id === selectedRoleId)
                return matchesSearch && matchesRole
            }),
        [eligibleUsers, searchText, selectedRoleId],
    )

    const selectedUsers = useMemo(
        () => userList.filter((u) => selectedIds.has(u.id)),
        [userList, selectedIds],
    )

    const toggleUser = (userId: number) => {
        setSelectedIds((prev) => {
            const next = new Set(prev)
            if (next.has(userId)) next.delete(userId)
            else next.add(userId)
            return next
        })
    }

    const toggleAll = () => {
        const allFilteredIds = filteredUsers.map((u) => u.id)
        const allSelected = allFilteredIds.every((id) => selectedIds.has(id))
        if (allSelected) {
            setSelectedIds((prev) => {
                const next = new Set(prev)
                allFilteredIds.forEach((id) => next.delete(id))
                return next
            })
        } else {
            setSelectedIds((prev) => {
                const next = new Set(prev)
                allFilteredIds.forEach((id) => next.add(id))
                return next
            })
        }
    }

    const setUserStatus = (userId: number, status: ArchiveStatus) => {
        setArchiveStatuses((prev) => ({ ...prev, [userId]: status }))
    }

    const handleArchive = async () => {
        setIsArchiving(true)

        const usersToArchive = userList.filter((u) => selectedIds.has(u.id))

        // Mark all as loading
        usersToArchive.forEach((u) => setUserStatus(u.id, 'loading'))

        let successCount = 0

        try {
            const response = await axiosInstance.post('user/bulk_archive/', {
                user_ids: usersToArchive.map((u) => u.id),
            })

            const apiResults: Array<{
                user_id: number
                success: boolean
                error?: string
            }> = response.data.results

            const successfulIds = new Set<number>()

            apiResults.forEach((result) => {
                if (result.success) {
                    successCount++
                    successfulIds.add(result.user_id)
                    setUserStatus(result.user_id, 'success')
                    // Keep Redux user list in sync
                    const user = usersToArchive.find(
                        (u) => u.id === result.user_id,
                    )
                    if (user) dispatch(removeUserFromList(user))
                } else {
                    setUserStatus(result.user_id, 'error')
                }
            })

            if (successfulIds.size > 0) {
                setSelectedIds((prev) => {
                    const next = new Set(prev)
                    successfulIds.forEach((id) => next.delete(id))
                    return next
                })
            }
        } catch (e: any) {
            const msg =
                e?.response?.data?.message ??
                'Request failed. Please try again.'
            usersToArchive.forEach((u) => setUserStatus(u.id, 'error'))
            toast.error(msg)
        }

        setIsArchiving(false)

        if (successCount > 0) {
            toast.success(
                `${successCount} user${
                    successCount > 1 ? 's' : ''
                } archived successfully!`,
            )
        }

        if (successCount === usersToArchive.length) {
            onClose()
        } else {
            setStep('select')
        }
    }

    const onClose = () => {
        setSelectedIds(new Set())
        setSearchText('')
        setSelectedRoleId('')
        setArchiveStatuses({})
        setIsArchiving(false)
        setStep('select')
        handleClose()
    }

    const allFilteredSelected =
        filteredUsers.length > 0 &&
        filteredUsers.every((u) => selectedIds.has(u.id))

    const someFilteredSelected =
        filteredUsers.some((u) => selectedIds.has(u.id)) && !allFilteredSelected

    const selectedCount = selectedIds.size

    return (
        <Modal
            open={open}
            style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            }}
            onClose={onClose}
        >
            <Slide direction="up" in={open}>
                <Paper
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        outline: 'none',
                        borderRadius: '20px',
                        width: 'min(800px, 92vw)',
                        height: '90vh',
                        overflow: 'hidden',
                    }}
                >
                    {/* ── Header ── */}
                    <Container
                        style={{
                            padding: theme.spacing(3, 3, 2, 3),
                            alignItems: 'center',
                            flexShrink: 0,
                        }}
                    >
                        <ArchiveIcon
                            style={{
                                marginRight: theme.spacing(1),
                                color: theme.palette.text.secondary,
                            }}
                        />
                        <span
                            style={{
                                ...typographyStyle(theme.typography.h5),
                                fontWeight: theme.typography.fontWeightBold,
                            }}
                        >
                            Bulk Archive Members
                        </span>
                        <div style={{ flex: 1 }} />
                        <Tooltip title="Cancel">
                            <IconButton onClick={onClose}>
                                <CloseIcon fontSize="large" />
                            </IconButton>
                        </Tooltip>
                    </Container>

                    <Divider
                        style={{ margin: theme.spacing(0, 3), flexShrink: 0 }}
                    />

                    {/* ── STEP 1: Select ── */}
                    {step === 'select' && (
                        <>
                            <Container
                                style={{
                                    padding: theme.spacing(1.5, 3, 0.5, 3),
                                    color: theme.palette.text.secondary,
                                    fontSize: 14,
                                    flexShrink: 0,
                                }}
                            >
                                Select the members you&apos;d like to remove
                                from your workspace. Workspace owners cannot be
                                archived.
                            </Container>

                            {!canEditUsers && (
                                <Container
                                    style={{
                                        margin: theme.spacing(0.5, 3, 0.5, 3),
                                        padding: theme.spacing(1, 2),
                                        backgroundColor:
                                            theme.palette.grey[100],
                                        borderRadius: theme.shape.borderRadius,
                                        border: `1px solid ${theme.palette.divider}`,
                                        fontSize: 13,
                                        color: theme.palette.text.secondary,
                                        flexShrink: 0,
                                    }}
                                >
                                    You do not have permission to archive users.
                                    Contact your workspace admin.
                                </Container>
                            )}

                            {/* Search + Role filter */}
                            <Container
                                style={{
                                    padding: theme.spacing(1, 3),
                                    gap: theme.spacing(1.5),
                                    flexShrink: 0,
                                }}
                            >
                                <TextField
                                    variant="outlined"
                                    size="small"
                                    placeholder="Search members..."
                                    value={searchText}
                                    onChange={(e) =>
                                        setSearchText(e.target.value)
                                    }
                                    style={{ flex: 1 }}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon
                                                    style={{
                                                        color:
                                                            theme.palette.text
                                                                .secondary,
                                                    }}
                                                />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                                <Select
                                    variant="outlined"
                                    value={selectedRoleId}
                                    onChange={(e) =>
                                        setSelectedRoleId(
                                            e.target.value as number | '',
                                        )
                                    }
                                    displayEmpty
                                    style={{
                                        minWidth: 180,
                                        height: 40,
                                        fontSize: 14,
                                    }}
                                >
                                    <MenuItem value="">
                                        <span
                                            style={{
                                                color:
                                                    theme.palette.text
                                                        .secondary,
                                            }}
                                        >
                                            All roles
                                        </span>
                                    </MenuItem>
                                    {(permissionRoles ?? []).map((role) => (
                                        <MenuItem key={role.id} value={role.id}>
                                            {role.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </Container>

                            {/* Select all row */}
                            <Container
                                style={{
                                    padding: theme.spacing(0.5, 3),
                                    alignItems: 'center',
                                    backgroundColor: theme.palette.grey[50],
                                    borderTop: `1px solid ${theme.palette.divider}`,
                                    borderBottom: `1px solid ${theme.palette.divider}`,
                                    flexShrink: 0,
                                }}
                            >
                                <Checkbox
                                    checked={allFilteredSelected}
                                    indeterminate={someFilteredSelected}
                                    onChange={toggleAll}
                                    disabled={
                                        filteredUsers.length === 0 ||
                                        !canEditUsers
                                    }
                                    style={{
                                        color:
                                            allFilteredSelected ||
                                            someFilteredSelected
                                                ? theme.palette.error.main
                                                : undefined,
                                    }}
                                />
                                <span
                                    style={{
                                        fontSize: 13,
                                        fontWeight: 600,
                                        color: theme.palette.text.secondary,
                                    }}
                                >
                                    {allFilteredSelected
                                        ? 'Deselect all'
                                        : 'Select all'}
                                    {filteredUsers.length > 0 &&
                                        ` (${filteredUsers.length} shown)`}
                                </span>
                                {selectedCount > 0 && (
                                    <span
                                        style={{
                                            marginLeft: theme.spacing(2),
                                            fontSize: 13,
                                            color: theme.palette.error.main,
                                            fontWeight: 500,
                                        }}
                                    >
                                        {selectedCount} selected
                                    </span>
                                )}
                            </Container>

                            {/* User list */}
                            <Container
                                direction="column"
                                scrollY
                                style={{
                                    flex: 1,
                                    minHeight: 0,
                                    overflowY: 'auto',
                                }}
                            >
                                {filteredUsers.length === 0 ? (
                                    <Container
                                        style={{
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            padding: theme.spacing(4),
                                            color: theme.palette.text.secondary,
                                            fontSize: 14,
                                        }}
                                    >
                                        {searchText || selectedRoleId !== ''
                                            ? 'No members match your filters.'
                                            : 'No eligible members to archive.'}
                                    </Container>
                                ) : (
                                    filteredUsers.map((user) => {
                                        const isSelected = selectedIds.has(
                                            user.id,
                                        )
                                        return (
                                            <Container
                                                key={user.id}
                                                style={{
                                                    alignItems: 'center',
                                                    padding: theme.spacing(
                                                        0.75,
                                                        3,
                                                    ),
                                                    borderBottom: `1px solid ${theme.palette.divider}`,
                                                    backgroundColor: isSelected
                                                        ? 'rgba(211,47,47,0.04)'
                                                        : 'transparent',
                                                    cursor: 'pointer',
                                                    transition:
                                                        'background-color 0.15s',
                                                }}
                                                onClick={() => {
                                                    if (canEditUsers)
                                                        toggleUser(user.id)
                                                }}
                                            >
                                                <Checkbox
                                                    checked={isSelected}
                                                    disabled={!canEditUsers}
                                                    onClick={(e) =>
                                                        e.stopPropagation()
                                                    }
                                                    onChange={() => {
                                                        if (canEditUsers)
                                                            toggleUser(user.id)
                                                    }}
                                                    style={{
                                                        color: isSelected
                                                            ? theme.palette
                                                                  .error.main
                                                            : undefined,
                                                    }}
                                                />
                                                <Container
                                                    direction="column"
                                                    style={{ flex: 1 }}
                                                >
                                                    <span
                                                        style={{
                                                            fontWeight: 500,
                                                            fontSize: 14,
                                                        }}
                                                    >
                                                        {user.name}
                                                    </span>
                                                    <span
                                                        style={{
                                                            fontSize: 12,
                                                            color:
                                                                theme.palette
                                                                    .text
                                                                    .secondary,
                                                        }}
                                                    >
                                                        {user.email}
                                                    </span>
                                                </Container>
                                                {user.permission_roles.length >
                                                    0 && (
                                                    <span
                                                        style={{
                                                            fontSize: 12,
                                                            color:
                                                                theme.palette
                                                                    .text
                                                                    .secondary,
                                                            marginRight: theme.spacing(
                                                                1,
                                                            ),
                                                        }}
                                                    >
                                                        {user.permission_roles
                                                            .map((r) => r.name)
                                                            .join(', ')}
                                                    </span>
                                                )}
                                            </Container>
                                        )
                                    })
                                )}
                            </Container>

                            <Divider
                                style={{
                                    margin: theme.spacing(0, 3),
                                    flexShrink: 0,
                                }}
                            />

                            {/* Footer */}
                            <Container
                                style={{
                                    padding: theme.spacing(2, 3),
                                    justifyContent: 'flex-end',
                                    alignItems: 'center',
                                    gap: theme.spacing(2),
                                    flexShrink: 0,
                                }}
                            >
                                <Button
                                    variant="outlined"
                                    onClick={onClose}
                                    style={{ textTransform: 'none' }}
                                >
                                    Cancel
                                </Button>
                                <Tooltip
                                    title={
                                        !canEditUsers
                                            ? 'You do not have permission to archive users'
                                            : ''
                                    }
                                >
                                    <span>
                                        <Button
                                            variant="contained"
                                            onClick={() => setStep('confirm')}
                                            disabled={
                                                selectedCount === 0 ||
                                                !canEditUsers
                                            }
                                            style={{
                                                textTransform: 'none',
                                                backgroundColor:
                                                    selectedCount === 0 ||
                                                    !canEditUsers
                                                        ? undefined
                                                        : theme.palette.error
                                                              .main,
                                                color:
                                                    selectedCount === 0 ||
                                                    !canEditUsers
                                                        ? undefined
                                                        : 'white',
                                                minWidth: 180,
                                            }}
                                        >
                                            {`Archive ${
                                                selectedCount > 0
                                                    ? selectedCount
                                                    : ''
                                            } Member${
                                                selectedCount !== 1 ? 's' : ''
                                            }`}
                                        </Button>
                                    </span>
                                </Tooltip>
                            </Container>
                        </>
                    )}

                    {/* ── STEP 2: Confirm ── */}
                    {step === 'confirm' && (
                        <>
                            {/* Big destructive count banner */}
                            <Container
                                direction="column"
                                style={{
                                    margin: theme.spacing(2, 3, 0, 3),
                                    padding: theme.spacing(2.5, 3),
                                    backgroundColor: theme.palette.error.main,
                                    borderRadius: theme.shape.borderRadius,
                                    alignItems: 'center',
                                    gap: theme.spacing(0.5),
                                    flexShrink: 0,
                                }}
                            >
                                <Container
                                    style={{
                                        alignItems: 'center',
                                        gap: theme.spacing(1),
                                    }}
                                >
                                    <WarningIcon
                                        style={{ color: 'white', fontSize: 22 }}
                                    />
                                    <span
                                        style={{
                                            fontWeight: 700,
                                            fontSize: 22,
                                            color: 'white',
                                            letterSpacing: '-0.5px',
                                        }}
                                    >
                                        Archive {selectedCount} Member
                                        {selectedCount !== 1 ? 's' : ''}?
                                    </span>
                                </Container>
                                <span
                                    style={{
                                        fontSize: 13,
                                        color: 'rgba(255,255,255,0.82)',
                                        textAlign: 'center',
                                    }}
                                >
                                    They will be removed from your workspace
                                    immediately and cannot log in. This cannot
                                    be undone.
                                </span>
                            </Container>

                            {/* Member list */}
                            <Container
                                style={{
                                    padding: theme.spacing(1.5, 3, 0.5, 3),
                                    fontWeight: 600,
                                    fontSize: 12,
                                    letterSpacing: '0.06em',
                                    textTransform: 'uppercase',
                                    color: theme.palette.text.secondary,
                                    flexShrink: 0,
                                }}
                            >
                                Members being removed
                            </Container>

                            <Container
                                direction="column"
                                scrollY
                                style={{
                                    flex: 1,
                                    minHeight: 0,
                                    overflowY: 'auto',
                                    margin: theme.spacing(0, 3),
                                    border: `1px solid ${theme.palette.divider}`,
                                    borderRadius: theme.shape.borderRadius,
                                    overflow: 'auto',
                                }}
                            >
                                {selectedUsers.map((user, i) => {
                                    const status =
                                        archiveStatuses[user.id] ?? 'idle'
                                    const initials = user.name
                                        .split(' ')
                                        .slice(0, 2)
                                        .map((w) => w[0]?.toUpperCase() ?? '')
                                        .join('')
                                    return (
                                        <Container
                                            key={user.id}
                                            style={{
                                                alignItems: 'center',
                                                padding: theme.spacing(1, 2),
                                                gap: theme.spacing(1.5),
                                                borderBottom:
                                                    i < selectedUsers.length - 1
                                                        ? `1px solid ${theme.palette.divider}`
                                                        : undefined,
                                                backgroundColor:
                                                    status === 'success'
                                                        ? 'rgba(0,140,133,0.06)'
                                                        : status === 'error'
                                                        ? 'rgba(211,47,47,0.06)'
                                                        : i % 2 === 0
                                                        ? 'transparent'
                                                        : theme.palette
                                                              .grey[50],
                                                transition:
                                                    'background-color 0.2s',
                                            }}
                                        >
                                            {/* Initials avatar */}
                                            <div
                                                style={{
                                                    width: 32,
                                                    height: 32,
                                                    borderRadius: '50%',
                                                    backgroundColor: `hsl(${
                                                        (user.name.charCodeAt(
                                                            0,
                                                        ) *
                                                            37) %
                                                        360
                                                    }, 55%, 62%)`,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    flexShrink: 0,
                                                    fontSize: 12,
                                                    fontWeight: 700,
                                                    color: 'white',
                                                    opacity:
                                                        status === 'success'
                                                            ? 0.5
                                                            : 1,
                                                }}
                                            >
                                                {initials}
                                            </div>

                                            <Container
                                                direction="column"
                                                style={{ flex: 1 }}
                                            >
                                                <span
                                                    style={{
                                                        fontWeight: 500,
                                                        fontSize: 14,
                                                    }}
                                                >
                                                    {user.name}
                                                </span>
                                                <span
                                                    style={{
                                                        fontSize: 12,
                                                        color:
                                                            theme.palette.text
                                                                .secondary,
                                                    }}
                                                >
                                                    {user.email}
                                                </span>
                                            </Container>

                                            {status === 'loading' && (
                                                <CircularProgress
                                                    size={16}
                                                    style={{
                                                        color:
                                                            theme.palette.error
                                                                .main,
                                                    }}
                                                />
                                            )}
                                            {status === 'success' && (
                                                <span
                                                    style={{
                                                        fontSize: 12,
                                                        color: '#008C85',
                                                        fontWeight: 500,
                                                    }}
                                                >
                                                    Archived
                                                </span>
                                            )}
                                            {status === 'error' && (
                                                <span
                                                    style={{
                                                        fontSize: 12,
                                                        color:
                                                            theme.palette.error
                                                                .main,
                                                        fontWeight: 500,
                                                    }}
                                                >
                                                    Failed
                                                </span>
                                            )}
                                        </Container>
                                    )
                                })}
                            </Container>

                            <Divider
                                style={{
                                    margin: theme.spacing(2, 3, 0, 3),
                                    flexShrink: 0,
                                }}
                            />

                            {/* Footer */}
                            <Container
                                style={{
                                    padding: theme.spacing(2, 3),
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    flexShrink: 0,
                                }}
                            >
                                <Button
                                    startIcon={<ArrowBackIcon />}
                                    onClick={() => setStep('select')}
                                    disabled={isArchiving}
                                    style={{
                                        textTransform: 'none',
                                        color: theme.palette.text.secondary,
                                    }}
                                >
                                    Go back
                                </Button>
                                <Button
                                    variant="contained"
                                    onClick={handleArchive}
                                    disabled={isArchiving}
                                    style={{
                                        textTransform: 'none',
                                        backgroundColor: isArchiving
                                            ? undefined
                                            : theme.palette.error.main,
                                        color: isArchiving
                                            ? undefined
                                            : 'white',
                                        minWidth: 220,
                                        fontWeight: 600,
                                    }}
                                >
                                    {isArchiving ? (
                                        <CircularProgress
                                            size={22}
                                            style={{ color: 'white' }}
                                        />
                                    ) : (
                                        `Yes, Remove ${selectedCount} Member${
                                            selectedCount !== 1 ? 's' : ''
                                        }`
                                    )}
                                </Button>
                            </Container>
                        </>
                    )}
                </Paper>
            </Slide>
        </Modal>
    )
}
